import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup, Label } from 'reactstrap';
import { Colors } from '../../../utils/budget-tool-specific';
import { setDocument, listenToDocument, listenToCollection } from '../../../utils/firestore-functions';
import { GetDB } from '../../../utils/database/firebase-budget-tool';
import { uploadLocationImage, deleteLocationImage, validateSpacesConfig } from '../../../utils/digitalOceanSpaces';
import styled from 'styled-components';

const SettingsAgenda = (props) => {
    const [agendaData, setAgendaData] = useState(null);
    const [locations, setLocations] = useState([]);
    const [activities, setActivities] = useState([]);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [locationForm, setLocationForm] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSection, setActiveSection] = useState('locations');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [modalTab, setModalTab] = useState('details');
    const [spacesConfigValid, setSpacesConfigValid] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [editingActivity, setEditingActivity] = useState(null);
    const [activityForm, setActivityForm] = useState({});
    const [activitySearchTerm, setActivitySearchTerm] = useState('');
    const budgetDB = GetDB();

    // Default values to use if no data exists in database
    const defaultAgendaData = {
        systemConfig: {
            defaultCurrency: 'USD',
            defaultTimezone: 'America/New_York'
        },
        uiPreferences: {
            defaultView: 'grid',
            itemsPerPage: 12
        }
    };

    const defaultLocationForm = {
        name: '',
        description: '',
        country: '',
        region: '',
        city: '',
        address: '',
        coordinates: { lat: '', lng: '' },
        timezone: 'America/New_York',
        currency: 'USD',
        languages: ['English'],
        season: '',
        amenities: [],
        capacity: '',
        pricePerPerson: '',
        contactInfo: {
            email: '',
            phone: '',
            website: ''
        },
        storageFolder: '',
        firestoreAccess: {
            projectId: '',
            collection: ''
        },
        images: [],
        isActive: true
    };

    const defaultActivityForm = {
        name: '',
        locationId: '',
        publicContent: {
            description: '',
            category: '',
            duration: '',
            icon: '',
            images: [],
            optionsAvailable: ''  // "3 chef options available", "5 speakers available", etc.
        },
        privateContent: {
            detailedDescription: '',
            specificDetails: '',
            exclusiveImages: [],
            availableOptions: [],  // Array of specific options with pricing
            contactInfo: {},
            pricing: {
                basePrice: '',
                costPerPerson: '',
                groupDiscounts: {},
                seasonalPricing: {}
            },
            availability: {
                calendar: {},
                blackoutDates: [],
                advanceBooking: ''
            },
            providerInfo: {
                name: '',
                credentials: [],
                specialInstructions: ''
            }
        },
        blockTypes: ['activity'],
        tags: [],
        maxParticipants: '',
        minParticipants: '',
        seasonality: '',
        timeOfDay: '',
        isActive: true,
        popularity: 0
    };

    useEffect(() => {
        // Listen to the Agenda document in the Settings collection
        const unsubscribe = listenToDocument(budgetDB, "Settings", "Agenda", (data) => {
            if (data) {
                // Merge with default data to ensure all fields exist
                const mergedData = {
                    ...defaultAgendaData,
                    ...data
                };
                setAgendaData(mergedData);
            } else {
                // If no data exists, use default values
                setAgendaData(defaultAgendaData);
            }
        });

        return unsubscribe;
    }, [budgetDB]);

    useEffect(() => {
        // Listen to locations collection
        const unsubscribe = listenToCollection(budgetDB, "agenda-locations", (data) => {
            setLocations(data || []);
        });

        return unsubscribe;
    }, [budgetDB]);

    useEffect(() => {
        // Listen to activities collection
        const unsubscribe = listenToCollection(budgetDB, "agenda-activities", (data) => {
            setActivities(data || []);
        });

        return unsubscribe;
    }, [budgetDB]);

    useEffect(() => {
        // Validate Digital Ocean Spaces configuration
        setSpacesConfigValid(validateSpacesConfig());
    }, []);

    const updateValue = (key, value) => {
        const newData = { ...agendaData };
        newData[key] = value;
        setAgendaData(newData);
        
        // Update the database
        setDocument(budgetDB, "Settings", "Agenda", newData);
    };

    const handleLocationFormChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setLocationForm(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setLocationForm(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const openLocationModal = (location = null) => {
        setEditingLocation(location);
        setLocationForm(location ? { ...location } : { ...defaultLocationForm });
        setModalTab('details');
        setShowLocationModal(true);
    };

    const closeLocationModal = () => {
        setShowLocationModal(false);
        setEditingLocation(null);
        setLocationForm({});
        setModalTab('details');
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!spacesConfigValid) {
            alert('Digital Ocean Spaces is not properly configured. Please check your environment variables.');
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file.');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Image size must be less than 5MB.');
            return;
        }

        setUploadingImage(true);
        
        try {
            const locationId = editingLocation?.id || `loc-${Date.now()}`;
            
            // Upload to Digital Ocean Spaces
            const imageUrl = await uploadLocationImage(file, locationId);
            
            // Update the form with the permanent URL
            const updatedImages = [...(locationForm.images || []), imageUrl];
            setLocationForm(prev => ({
                ...prev,
                images: updatedImages
            }));
            
            console.log('Image uploaded successfully:', imageUrl);
            
        } catch (error) {
            console.error('Error uploading image:', error);
            alert(`Failed to upload image: ${error.message}`);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleActivityImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!spacesConfigValid) {
            alert('Digital Ocean Spaces is not properly configured. Please check your environment variables.');
            return;
        }

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Image size must be less than 5MB.');
            return;
        }

        setUploadingImage(true);
        
        try {
            const activityId = editingActivity?.id || `act-${Date.now()}`;
            const imageUrl = await uploadLocationImage(file, `activities/${activityId}`);
            
            const updatedImages = [...(activityForm.publicContent?.images || []), imageUrl];
            setActivityForm(prev => ({
                ...prev,
                publicContent: {
                    ...prev.publicContent,
                    images: updatedImages
                }
            }));
            
            console.log('Activity image uploaded successfully:', imageUrl);
            
        } catch (error) {
            console.error('Error uploading activity image:', error);
            alert(`Failed to upload image: ${error.message}`);
        } finally {
            setUploadingImage(false);
        }
    };

    const removeActivityImage = async (index) => {
        const imageToDelete = activityForm.publicContent?.images?.[index];
        if (!imageToDelete) return;

        try {
            if (!imageToDelete.startsWith('blob:') && spacesConfigValid) {
                await deleteLocationImage(imageToDelete);
                console.log('Activity image deleted from Digital Ocean Spaces:', imageToDelete);
            }
        } catch (error) {
            console.error('Error deleting activity image from Spaces:', error);
        }

        const updatedImages = activityForm.publicContent?.images?.filter((_, i) => i !== index) || [];
        setActivityForm(prev => ({
            ...prev,
            publicContent: {
                ...prev.publicContent,
                images: updatedImages
            }
        }));
    };

    const removeImage = async (index) => {
        const imageToDelete = locationForm.images?.[index];
        if (!imageToDelete) return;

        try {
            // Delete from Digital Ocean Spaces if it's not a blob URL
            if (!imageToDelete.startsWith('blob:') && spacesConfigValid) {
                await deleteLocationImage(imageToDelete);
                console.log('Image deleted from Digital Ocean Spaces:', imageToDelete);
            }
        } catch (error) {
            console.error('Error deleting image from Spaces:', error);
            // Continue with local removal even if cloud deletion fails
        }

        // Remove from local state
        const updatedImages = locationForm.images?.filter((_, i) => i !== index) || [];
        setLocationForm(prev => ({
            ...prev,
            images: updatedImages
        }));
    };

    const saveLocation = async () => {
        try {
            const locationId = editingLocation?.id || `loc-${Date.now()}`;
            const locationData = {
                ...locationForm,
                updatedAt: new Date(),
                lastModifiedBy: 'admin' // In real app, use actual user ID
            };

            if (!editingLocation) {
                locationData.createdAt = new Date();
                locationData.createdBy = 'admin'; // In real app, use actual user ID
                locationData.id = locationId;
            }

            await setDocument(budgetDB, "agenda-locations", locationId, locationData);
            closeLocationModal();
        } catch (error) {
            console.error('Error saving location:', error);
        }
    };

    const deleteLocation = async (locationId) => {
        if (window.confirm('Are you sure you want to delete this location?')) {
            try {
                await setDocument(budgetDB, "agenda-locations", locationId, { isActive: false, updatedAt: new Date() });
            } catch (error) {
                console.error('Error deleting location:', error);
            }
        }
    };

    const filteredLocations = locations.filter(location => 
        location.isActive !== false && 
        (location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         location.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         location.country?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const getLocationActivities = (locationId) => {
        return activities.filter(activity => 
            activity.locationId === locationId && activity.isActive !== false
        );
    };

    const handleActivityFormChange = (field, value) => {
        if (field.includes('.')) {
            const fieldParts = field.split('.');
            setActivityForm(prev => {
                const updated = { ...prev };
                let current = updated;
                
                for (let i = 0; i < fieldParts.length - 1; i++) {
                    if (!current[fieldParts[i]]) current[fieldParts[i]] = {};
                    current = current[fieldParts[i]];
                }
                
                current[fieldParts[fieldParts.length - 1]] = value;
                return updated;
            });
        } else {
            setActivityForm(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const openActivityModal = (activity = null) => {
        setEditingActivity(activity);
        
        if (activity) {
            // Ensure existing options have IDs for proper React key handling
            const activityWithIds = { ...activity };
            if (activityWithIds.privateContent?.availableOptions) {
                activityWithIds.privateContent.availableOptions = activityWithIds.privateContent.availableOptions.map((option, index) => ({
                    ...option,
                    id: option.id || `existing-${index}-${Date.now()}`
                }));
            }
            setActivityForm(activityWithIds);
        } else {
            setActivityForm({ ...defaultActivityForm });
        }
        
        setShowActivityModal(true);
    };

    const closeActivityModal = () => {
        setShowActivityModal(false);
        setEditingActivity(null);
        setActivityForm({});
    };

    const addPostDepositOption = () => {
        const newOption = {
            id: Date.now(),
            name: '',
            price: '',
            description: '',
            image: ''
        };
        
        const currentOptions = activityForm.privateContent?.availableOptions || [];
        const updatedOptions = [...currentOptions, newOption];
        
        setActivityForm(prev => ({
            ...prev,
            privateContent: {
                ...prev.privateContent,
                availableOptions: updatedOptions
            }
        }));
    };

    const removePostDepositOption = (optionId) => {
        const currentOptions = activityForm.privateContent?.availableOptions || [];
        const updatedOptions = currentOptions.filter(option => option.id !== optionId);
        
        setActivityForm(prev => ({
            ...prev,
            privateContent: {
                ...prev.privateContent,
                availableOptions: updatedOptions
            }
        }));
    };

    const updatePostDepositOption = (optionId, field, value) => {
        const currentOptions = activityForm.privateContent?.availableOptions || [];
        const updatedOptions = currentOptions.map(option => 
            option.id === optionId 
                ? { ...option, [field]: value }
                : option
        );
        
        setActivityForm(prev => ({
            ...prev,
            privateContent: {
                ...prev.privateContent,
                availableOptions: updatedOptions
            }
        }));
    };

    const handleOptionImageUpload = async (event, optionId) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!spacesConfigValid) {
            alert('Digital Ocean Spaces is not properly configured. Please check your environment variables.');
            return;
        }

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Image size must be less than 5MB.');
            return;
        }

        setUploadingImage(true);
        
        try {
            const activityId = editingActivity?.id || `act-${Date.now()}`;
            const imageUrl = await uploadLocationImage(file, `activities/${activityId}/options`);
            
            updatePostDepositOption(optionId, 'image', imageUrl);
            
            console.log('Option image uploaded successfully:', imageUrl);
            
        } catch (error) {
            console.error('Error uploading option image:', error);
            alert(`Failed to upload image: ${error.message}`);
        } finally {
            setUploadingImage(false);
        }
    };

    const removeOptionImage = async (optionId) => {
        const currentOptions = activityForm.privateContent?.availableOptions || [];
        const option = currentOptions.find(opt => opt.id === optionId);
        
        if (option && option.image) {
            try {
                if (!option.image.startsWith('blob:') && spacesConfigValid) {
                    await deleteLocationImage(option.image);
                    console.log('Option image deleted from Digital Ocean Spaces:', option.image);
                }
            } catch (error) {
                console.error('Error deleting option image from Spaces:', error);
            }
        }

        updatePostDepositOption(optionId, 'image', '');
    };

    const saveActivity = async () => {
        try {
            const activityId = editingActivity?.id || `act-${Date.now()}`;
            const activityData = {
                ...activityForm,
                updatedAt: new Date(),
                lastModifiedBy: 'admin'
            };

            if (!editingActivity) {
                activityData.createdAt = new Date();
                activityData.createdBy = 'admin';
                activityData.id = activityId;
            }

            await setDocument(budgetDB, "agenda-activities", activityId, activityData);
            closeActivityModal();
        } catch (error) {
            console.error('Error saving activity:', error);
        }
    };

    const deleteActivity = async (activityId) => {
        if (window.confirm('Are you sure you want to delete this activity?')) {
            try {
                await setDocument(budgetDB, "agenda-activities", activityId, { isActive: false, updatedAt: new Date() });
            } catch (error) {
                console.error('Error deleting activity:', error);
            }
        }
    };

    const filteredActivities = activities.filter(activity => 
        activity.isActive !== false && 
        (activity.name?.toLowerCase().includes(activitySearchTerm.toLowerCase()) ||
         activity.publicContent?.category?.toLowerCase().includes(activitySearchTerm.toLowerCase()) ||
         locations.find(loc => loc.id === activity.locationId)?.name?.toLowerCase().includes(activitySearchTerm.toLowerCase()))
    );

    if (!agendaData) {
        return <div>Loading agenda settings...</div>;
    }

    return ( 
        <React.Fragment>
            <SettingsContainer>
                <NotesSection>
                    <NotesTitle>Agenda Tool Settings</NotesTitle>
                    <NotesText>
                        Configure locations, activities, and system settings for the Agenda tool. This tool helps manage retreat schedules and activities.
                    </NotesText>
                </NotesSection>

                <TabNavigation>
                    <TabButton 
                        type="button"
                        active={activeSection === 'locations'} 
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveSection('locations');
                        }}
                    >
                        Locations
                    </TabButton>
                    <TabButton 
                        type="button"
                        active={activeSection === 'activities'} 
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveSection('activities');
                        }}
                    >
                        Activities
                    </TabButton>
                    <TabButton 
                        type="button"
                        active={activeSection === 'system'} 
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveSection('system');
                        }}
                    >
                        System Config
                    </TabButton>
                </TabNavigation>

                {activeSection === 'locations' && (
                    <SectionContainer>
                        <SectionHeader>
                            <SectionTitle>Locations Management</SectionTitle>
                            <HeaderActions>
                                <SearchInput
                                    type="text"
                                    placeholder="Search locations..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Button color="primary" onClick={() => openLocationModal()}>
                                    Add Location
                                </Button>
                            </HeaderActions>
                        </SectionHeader>

                        <LocationsGrid>
                            {filteredLocations.map(location => {
                                const locationActivities = getLocationActivities(location.id);
                                return (
                                    <LocationCard key={location.id}>
                                        {location.images && location.images.length > 0 && (
                                            <LocationImage src={location.images[0]} alt={location.name} />
                                        )}
                                        <LocationCardHeader>
                                            <LocationName>{location.name}</LocationName>
                                            <LocationActions>
                                                <Button size="sm" color="info" onClick={() => openLocationModal(location)}>
                                                    Edit
                                                </Button>
                                                <Button size="sm" color="danger" onClick={() => deleteLocation(location.id)}>
                                                    Delete
                                                </Button>
                                            </LocationActions>
                                        </LocationCardHeader>
                                        <LocationDetails>
                                            <LocationDetail><strong>City:</strong> {location.city}</LocationDetail>
                                            <LocationDetail><strong>Country:</strong> {location.country}</LocationDetail>
                                            <LocationDetail><strong>Capacity:</strong> {location.capacity}</LocationDetail>
                                            <LocationDetail><strong>Price per Person:</strong> {location.pricePerPerson ? `$${location.pricePerPerson}` : 'Not set'}</LocationDetail>
                                            <LocationDetail><strong>Activities:</strong> {locationActivities.length}</LocationDetail>
                                        </LocationDetails>
                                    </LocationCard>
                                );
                            })}
                            {filteredLocations.length === 0 && (
                                <EmptyState>
                                    No locations found. Click "Add Location" to create your first location.
                                </EmptyState>
                            )}
                        </LocationsGrid>
                    </SectionContainer>
                )}

                {activeSection === 'activities' && (
                    <SectionContainer>
                        <SectionHeader>
                            <SectionTitle>Activities Management</SectionTitle>
                            <HeaderActions>
                                <SearchInput
                                    type="text"
                                    placeholder="Search activities..."
                                    value={activitySearchTerm}
                                    onChange={(e) => setActivitySearchTerm(e.target.value)}
                                />
                                <Button color="primary" onClick={() => openActivityModal()}>
                                    Add Activity
                                </Button>
                            </HeaderActions>
                        </SectionHeader>

                        <ActivitiesGrid>
                            {filteredActivities.map(activity => {
                                const location = locations.find(loc => loc.id === activity.locationId);
                                return (
                                    <ActivityCard key={activity.id}>
                                        {activity.publicContent?.images && activity.publicContent.images.length > 0 && (
                                            <ActivityImage src={activity.publicContent.images[0]} alt={activity.name} />
                                        )}
                                        <ActivityCardHeader>
                                            <ActivityName>{activity.name}</ActivityName>
                                            <ActivityActions>
                                                <Button size="sm" color="info" onClick={() => openActivityModal(activity)}>
                                                    Edit
                                                </Button>
                                                <Button size="sm" color="danger" onClick={() => deleteActivity(activity.id)}>
                                                    Delete
                                                </Button>
                                            </ActivityActions>
                                        </ActivityCardHeader>
                                        <ActivityDetails>
                                            <ActivityDetail><strong>Location:</strong> {location?.name || 'Not assigned'}</ActivityDetail>
                                            <ActivityDetail><strong>Category:</strong> {activity.publicContent?.category || 'Not set'}</ActivityDetail>
                                            <ActivityDetail><strong>Duration:</strong> {activity.publicContent?.duration ? `${activity.publicContent.duration}h` : 'Not set'}</ActivityDetail>
                                            <ActivityDetail><strong>Max Participants:</strong> {activity.maxParticipants || 'Not set'}</ActivityDetail>
                                            {activity.publicContent?.optionsAvailable && (
                                                <ActivityDetail><strong>Options:</strong> {activity.publicContent.optionsAvailable}</ActivityDetail>
                                            )}
                                            <ActivityDetail><strong>Base Price:</strong> ${activity.privateContent?.pricing?.basePrice || 'Not set'}</ActivityDetail>
                                            <ActivityDetail><strong>Cost per Person:</strong> ${activity.privateContent?.pricing?.costPerPerson || 'Not set'}</ActivityDetail>
                                        </ActivityDetails>
                                    </ActivityCard>
                                );
                            })}
                            {filteredActivities.length === 0 && (
                                <EmptyState>
                                    No activities found. Click "Add Activity" to create your first activity.
                                </EmptyState>
                            )}
                        </ActivitiesGrid>
                    </SectionContainer>
                )}

                {activeSection === 'system' && (
                    <SectionContainer>
                        <SectionTitle>System Configuration</SectionTitle>
                        <p>System configuration coming soon...</p>
                    </SectionContainer>
                )}

                {/* Location Modal */}
                <Modal isOpen={showLocationModal} toggle={closeLocationModal} size="lg">
                    <ModalHeader toggle={closeLocationModal}>
                        {editingLocation ? 'Edit Location' : 'Add New Location'}
                    </ModalHeader>
                    <ModalBody>
                        <ModalTabs>
                            <ModalTabButton 
                                active={modalTab === 'details'} 
                                onClick={() => setModalTab('details')}
                            >
                                Details
                            </ModalTabButton>
                            <ModalTabButton 
                                active={modalTab === 'images'} 
                                onClick={() => setModalTab('images')}
                            >
                                Images
                            </ModalTabButton>
                            {editingLocation && (
                                <ModalTabButton 
                                    active={modalTab === 'activities'} 
                                    onClick={() => setModalTab('activities')}
                                >
                                    Activities ({getLocationActivities(editingLocation.id).length})
                                </ModalTabButton>
                            )}
                        </ModalTabs>

                        {modalTab === 'details' && (
                            <div>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Location Name *</Label>
                                    <Input
                                        value={locationForm.name || ''}
                                        onChange={(e) => handleLocationFormChange('name', e.target.value)}
                                        placeholder="Villa Montemayor"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Capacity *</Label>
                                    <Input
                                        type="number"
                                        value={locationForm.capacity || ''}
                                        onChange={(e) => handleLocationFormChange('capacity', parseInt(e.target.value))}
                                        placeholder="50"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label>Description *</Label>
                            <Input
                                type="textarea"
                                rows="3"
                                value={locationForm.description || ''}
                                onChange={(e) => handleLocationFormChange('description', e.target.value)}
                                placeholder="Brief description of the location..."
                            />
                        </FormGroup>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Country *</Label>
                                    <Input
                                        value={locationForm.country || ''}
                                        onChange={(e) => handleLocationFormChange('country', e.target.value)}
                                        placeholder="Costa Rica"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Region *</Label>
                                    <Input
                                        value={locationForm.region || ''}
                                        onChange={(e) => handleLocationFormChange('region', e.target.value)}
                                        placeholder="Guanacaste"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>City *</Label>
                                    <Input
                                        value={locationForm.city || ''}
                                        onChange={(e) => handleLocationFormChange('city', e.target.value)}
                                        placeholder="Tamarindo"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label>Address *</Label>
                            <Input
                                value={locationForm.address || ''}
                                onChange={(e) => handleLocationFormChange('address', e.target.value)}
                                placeholder="Full address"
                            />
                        </FormGroup>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Latitude</Label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={locationForm.coordinates?.lat || ''}
                                        onChange={(e) => handleLocationFormChange('coordinates.lat', parseFloat(e.target.value))}
                                        placeholder="10.2995"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Longitude</Label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={locationForm.coordinates?.lng || ''}
                                        onChange={(e) => handleLocationFormChange('coordinates.lng', parseFloat(e.target.value))}
                                        placeholder="-85.8366"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Timezone</Label>
                                    <Input
                                        type="select"
                                        value={locationForm.timezone || 'America/New_York'}
                                        onChange={(e) => handleLocationFormChange('timezone', e.target.value)}
                                    >
                                        <option value="America/New_York">EST (New York)</option>
                                        <option value="America/Chicago">CST (Chicago)</option>
                                        <option value="America/Denver">MST (Denver)</option>
                                        <option value="America/Los_Angeles">PST (Los Angeles)</option>
                                        <option value="America/Costa_Rica">CST (Costa Rica)</option>
                                        <option value="Europe/London">GMT (London)</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Currency</Label>
                                    <Input
                                        type="select"
                                        value={locationForm.currency || 'USD'}
                                        onChange={(e) => handleLocationFormChange('currency', e.target.value)}
                                    >
                                        <option value="USD">USD</option>
                                        <option value="EUR">EUR</option>
                                        <option value="GBP">GBP</option>
                                        <option value="CRC">CRC (Costa Rica)</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Price per Person</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={locationForm.pricePerPerson || ''}
                                        onChange={(e) => handleLocationFormChange('pricePerPerson', parseFloat(e.target.value) || '')}
                                        placeholder="299.00"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Contact Email</Label>
                                    <Input
                                        type="email"
                                        value={locationForm.contactInfo?.email || ''}
                                        onChange={(e) => handleLocationFormChange('contactInfo.email', e.target.value)}
                                        placeholder="contact@location.com"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Contact Phone</Label>
                                    <Input
                                        value={locationForm.contactInfo?.phone || ''}
                                        onChange={(e) => handleLocationFormChange('contactInfo.phone', e.target.value)}
                                        placeholder="+1 (555) 123-4567"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Website</Label>
                                    <Input
                                        type="url"
                                        value={locationForm.contactInfo?.website || ''}
                                        onChange={(e) => handleLocationFormChange('contactInfo.website', e.target.value)}
                                        placeholder="https://location.com"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                            </div>
                        )}

                        {modalTab === 'images' && (
                            <div>
                                {!spacesConfigValid && (
                                    <ConfigWarning>
                                        <strong>⚠️ Digital Ocean Spaces Not Configured</strong>
                                        <br />
                                        Image upload requires Digital Ocean Spaces configuration. Please add the required environment variables to your .env file.
                                    </ConfigWarning>
                                )}
                                
                                <FormGroup>
                                    <Label>Upload Images</Label>
                                    <ImageUploadContainer disabled={!spacesConfigValid}>
                                        <ImageUploadButton disabled={!spacesConfigValid}>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                disabled={uploadingImage || !spacesConfigValid}
                                                style={{ display: 'none' }}
                                                id="image-upload"
                                            />
                                            <label htmlFor="image-upload" style={{ 
                                                cursor: (!spacesConfigValid || uploadingImage) ? 'not-allowed' : 'pointer', 
                                                display: 'block',
                                                opacity: (!spacesConfigValid || uploadingImage) ? 0.6 : 1
                                            }}>
                                                {uploadingImage ? 'Uploading...' : spacesConfigValid ? 'Choose Image' : 'Configure Spaces First'}
                                            </label>
                                        </ImageUploadButton>
                                    </ImageUploadContainer>
                                    {spacesConfigValid && (
                                        <small style={{ color: '#6c757d', marginTop: '8px', display: 'block' }}>
                                            Supported formats: JPG, PNG, GIF. Max size: 5MB.
                                        </small>
                                    )}
                                </FormGroup>

                                <ImageGallery>
                                    {locationForm.images?.map((imageUrl, index) => (
                                        <ImagePreviewContainer key={index}>
                                            <ImagePreview src={imageUrl} alt={`Location ${index + 1}`} />
                                            <ImageRemoveButton onClick={() => removeImage(index)}>
                                                ×
                                            </ImageRemoveButton>
                                        </ImagePreviewContainer>
                                    ))}
                                    {(!locationForm.images || locationForm.images.length === 0) && (
                                        <EmptyImageState>
                                            No images uploaded yet. Click "Choose Image" to add photos of this location.
                                        </EmptyImageState>
                                    )}
                                </ImageGallery>
                            </div>
                        )}

                        {modalTab === 'activities' && editingLocation && (
                            <div>
                                <ActivityList>
                                    {getLocationActivities(editingLocation.id).map(activity => (
                                        <ActivityItem key={activity.id}>
                                            <ActivityName>{activity.name}</ActivityName>
                                            <ActivityDetails>
                                                <ActivityDetail>Category: {activity.publicContent?.category || 'Not set'}</ActivityDetail>
                                                <ActivityDetail>Duration: {activity.publicContent?.duration ? `${activity.publicContent.duration}h` : 'Not set'}</ActivityDetail>
                                                <ActivityDetail>Max Participants: {activity.maxParticipants || 'Not set'}</ActivityDetail>
                                                {activity.publicContent?.optionsAvailable && (
                                                    <ActivityDetail>Options: {activity.publicContent.optionsAvailable}</ActivityDetail>
                                                )}
                                                <ActivityDetail>Base Price: ${activity.privateContent?.pricing?.basePrice || 'Not set'}</ActivityDetail>
                                            </ActivityDetails>
                                        </ActivityItem>
                                    ))}
                                    {getLocationActivities(editingLocation.id).length === 0 && (
                                        <EmptyActivityState>
                                            No activities assigned to this location yet. Create activities and assign them to this location.
                                        </EmptyActivityState>
                                    )}
                                </ActivityList>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={closeLocationModal}>Cancel</Button>
                        <Button color="primary" onClick={saveLocation}>
                            {editingLocation ? 'Update' : 'Create'} Location
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Activity Modal */}
                <Modal isOpen={showActivityModal} toggle={closeActivityModal} size="lg">
                    <ModalHeader toggle={closeActivityModal}>
                        {editingActivity ? 'Edit Activity' : 'Add New Activity'}
                    </ModalHeader>
                    <ModalBody>
                        <Row>
                            <Col md={8}>
                                <FormGroup>
                                    <Label>Activity Name *</Label>
                                    <Input
                                        value={activityForm.name || ''}
                                        onChange={(e) => handleActivityFormChange('name', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Location *</Label>
                                    <Input
                                        type="select"
                                        value={activityForm.locationId || ''}
                                        onChange={(e) => handleActivityFormChange('locationId', e.target.value)}
                                    >
                                        <option value="">Select Location</option>
                                        {locations.filter(loc => loc.isActive !== false).map(location => (
                                            <option key={location.id} value={location.id}>
                                                {location.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label>Public Description *</Label>
                            <Input
                                type="textarea"
                                rows="3"
                                value={activityForm.publicContent?.description || ''}
                                onChange={(e) => handleActivityFormChange('publicContent.description', e.target.value)}
                            />
                            <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                                This description is visible to all users before payment.
                            </small>
                        </FormGroup>

                        <FormGroup>
                            <Label>Options Available (Public Preview)</Label>
                            <Input
                                value={activityForm.publicContent?.optionsAvailable || ''}
                                onChange={(e) => handleActivityFormChange('publicContent.optionsAvailable', e.target.value)}
                            />
                            <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                                e.g., "3 chef options available", "5 speakers to choose from", "Multiple venue options"
                            </small>
                        </FormGroup>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Category</Label>
                                    <Input
                                        type="select"
                                        value={activityForm.publicContent?.category || ''}
                                        onChange={(e) => handleActivityFormChange('publicContent.category', e.target.value)}
                                    >
                                        <option value="">Select Category</option>
                                        <option value="Adventure">Adventure</option>
                                        <option value="Cultural">Cultural</option>
                                        <option value="Wellness">Wellness</option>
                                        <option value="Educational">Educational</option>
                                        <option value="Recreational">Recreational</option>
                                        <option value="Culinary">Culinary</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Duration (hours)</Label>
                                    <Input
                                        type="number"
                                        min="0.5"
                                        step="0.5"
                                        value={activityForm.publicContent?.duration || ''}
                                        onChange={(e) => handleActivityFormChange('publicContent.duration', parseFloat(e.target.value) || '')}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Time of Day</Label>
                                    <Input
                                        type="select"
                                        value={activityForm.timeOfDay || ''}
                                        onChange={(e) => handleActivityFormChange('timeOfDay', e.target.value)}
                                    >
                                        <option value="">Select Time</option>
                                        <option value="Morning">Morning</option>
                                        <option value="Afternoon">Afternoon</option>
                                        <option value="Evening">Evening</option>
                                        <option value="Any">Any Time</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Min Participants</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={activityForm.minParticipants || ''}
                                        onChange={(e) => handleActivityFormChange('minParticipants', parseInt(e.target.value) || '')}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Max Participants</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={activityForm.maxParticipants || ''}
                                        onChange={(e) => handleActivityFormChange('maxParticipants', parseInt(e.target.value) || '')}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Base Price</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={activityForm.privateContent?.pricing?.basePrice || ''}
                                        onChange={(e) => handleActivityFormChange('privateContent.pricing.basePrice', parseFloat(e.target.value) || '')}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Cost per Person</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={activityForm.privateContent?.pricing?.costPerPerson || ''}
                                        onChange={(e) => handleActivityFormChange('privateContent.pricing.costPerPerson', parseFloat(e.target.value) || '')}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label>Activity Image</Label>
                            <ImageUploadContainer>
                                <ImageUploadButton>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleActivityImageUpload}
                                        disabled={uploadingImage}
                                        style={{ display: 'none' }}
                                        id="activity-image-upload"
                                    />
                                    <label htmlFor="activity-image-upload" style={{ cursor: 'pointer', display: 'block' }}>
                                        {uploadingImage ? 'Uploading...' : 'Choose Activity Image'}
                                    </label>
                                </ImageUploadButton>
                            </ImageUploadContainer>
                            
                            {activityForm.publicContent?.images && activityForm.publicContent.images.length > 0 && (
                                <ImageGallery>
                                    {activityForm.publicContent.images.map((imageUrl, index) => (
                                        <ImagePreviewContainer key={index}>
                                            <ImagePreview src={imageUrl} alt={`Activity ${index + 1}`} />
                                            <ImageRemoveButton onClick={() => removeActivityImage(index)}>
                                                ×
                                            </ImageRemoveButton>
                                        </ImagePreviewContainer>
                                    ))}
                                </ImageGallery>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>Detailed Description (Post-Deposit Content)</Label>
                            <Input
                                type="textarea"
                                rows="4"
                                value={activityForm.privateContent?.detailedDescription || ''}
                                onChange={(e) => handleActivityFormChange('privateContent.detailedDescription', e.target.value)}
                            />
                            <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                                Detailed information shown only after client pays deposit.
                            </small>
                        </FormGroup>

                        <FormGroup>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <Label style={{ marginBottom: 0 }}>Post-Deposit Options</Label>
                                <Button color="success" size="sm" onClick={addPostDepositOption}>
                                    + Add Option
                                </Button>
                            </div>
                            <small style={{ color: '#6c757d', marginBottom: '15px', display: 'block' }}>
                                These specific options (chefs, speakers, venues, etc.) will only be visible after deposit payment.
                            </small>
                            
                            <OptionsContainer>
                                {activityForm.privateContent?.availableOptions?.map((option, index) => (
                                    <OptionCard key={option.id || index}>
                                        <OptionHeader>
                                            <h6 style={{ margin: 0, color: Colors.primaryDark }}>Option {index + 1}</h6>
                                            <Button 
                                                color="danger" 
                                                size="sm" 
                                                onClick={() => removePostDepositOption(option.id || index)}
                                            >
                                                Remove
                                            </Button>
                                        </OptionHeader>
                                        
                                        <Row>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label>Name *</Label>
                                                    <Input
                                                        value={option.name || ''}
                                                        onChange={(e) => updatePostDepositOption(option.id || index, 'name', e.target.value)}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label>Price</Label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={option.price || ''}
                                                        onChange={(e) => updatePostDepositOption(option.id || index, 'price', parseFloat(e.target.value) || '')}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        
                                        <FormGroup>
                                            <Label>Description</Label>
                                            <Input
                                                type="textarea"
                                                rows="3"
                                                value={option.description || ''}
                                                onChange={(e) => updatePostDepositOption(option.id || index, 'description', e.target.value)}
                                            />
                                        </FormGroup>

                                        <FormGroup>
                                            <Label>Option Image</Label>
                                            <ImageUploadContainer>
                                                <ImageUploadButton>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleOptionImageUpload(e, option.id || index)}
                                                        disabled={uploadingImage}
                                                        style={{ display: 'none' }}
                                                        id={`option-image-upload-${option.id || index}`}
                                                    />
                                                    <label htmlFor={`option-image-upload-${option.id || index}`} style={{ cursor: 'pointer', display: 'block' }}>
                                                        {uploadingImage ? 'Uploading...' : 'Choose Image'}
                                                    </label>
                                                </ImageUploadButton>
                                            </ImageUploadContainer>
                                            
                                            {option.image && (
                                                <div style={{ marginTop: '10px' }}>
                                                    <ImagePreviewContainer>
                                                        <ImagePreview src={option.image} alt={option.name} style={{ height: '100px' }} />
                                                        <ImageRemoveButton onClick={() => removeOptionImage(option.id || index)}>
                                                            ×
                                                        </ImageRemoveButton>
                                                    </ImagePreviewContainer>
                                                </div>
                                            )}
                                        </FormGroup>
                                    </OptionCard>
                                ))}
                                
                                {(!activityForm.privateContent?.availableOptions || activityForm.privateContent.availableOptions.length === 0) && (
                                    <EmptyOptionsState>
                                        No post-deposit options added yet. Click "Add Option" to create chef choices, speaker selections, venue options, etc.
                                    </EmptyOptionsState>
                                )}
                            </OptionsContainer>
                        </FormGroup>

                        <hr style={{ margin: '30px 0 20px 0', borderTop: '2px solid #e9ecef' }} />
                        
                        <h6 style={{ color: Colors.primaryDark, marginBottom: '20px', fontWeight: '600' }}>
                            Internal Use Only
                        </h6>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Provider Name</Label>
                                    <Input
                                        value={activityForm.privateContent?.providerInfo?.name || ''}
                                        onChange={(e) => handleActivityFormChange('privateContent.providerInfo.name', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Advance Booking (days)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={activityForm.privateContent?.availability?.advanceBooking || ''}
                                        onChange={(e) => handleActivityFormChange('privateContent.availability.advanceBooking', parseInt(e.target.value) || '')}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label>Special Instructions</Label>
                            <Input
                                type="textarea"
                                rows="3"
                                value={activityForm.privateContent?.providerInfo?.specialInstructions || ''}
                                onChange={(e) => handleActivityFormChange('privateContent.providerInfo.specialInstructions', e.target.value)}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={closeActivityModal}>Cancel</Button>
                        <Button color="primary" onClick={saveActivity}>
                            {editingActivity ? 'Update' : 'Create'} Activity
                        </Button>
                    </ModalFooter>
                </Modal>
            </SettingsContainer>
        </React.Fragment>
    );
};
 
export default SettingsAgenda;

const SettingsContainer = styled.div`
    padding: 20px 0;
`;

const SectionContainer = styled.div`
    margin-bottom: 30px;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
`;

const SectionTitle = styled.h5`
    color: ${Colors.primaryDark};
    margin-bottom: 20px;
    font-weight: 600;
    border-bottom: 2px solid ${Colors.primaryAccent};
    padding-bottom: 8px;
`;

const NotesSection = styled.div`
    margin-bottom: 30px;
    padding: 20px;
    background-color: #e8f4fd;
    border-radius: 8px;
    border: 1px solid #b3d9f2;
`;

const NotesTitle = styled.h4`
    color: ${Colors.primaryDark};
    margin-bottom: 15px;
    font-weight: 600;
    border-bottom: 2px solid ${Colors.primaryAccent};
    padding-bottom: 8px;
`;

const NotesText = styled.p`
    color: ${Colors.primaryDark};
    font-size: 16px;
    margin-bottom: 15px;
    font-weight: 500;
`;

const TabNavigation = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    border-bottom: 1px solid #e9ecef;
`;

const TabButton = styled.button`
    padding: 12px 20px;
    border: none;
    background: ${props => props.active ? Colors.primaryAccent : 'transparent'};
    color: ${props => props.active ? 'white' : Colors.primaryDark};
    border-radius: 8px 8px 0 0;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s ease;
    
    &:hover {
        background: ${props => props.active ? Colors.primaryAccent : '#f8f9fa'};
    }
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;
`;

const HeaderActions = styled.div`
    display: flex;
    gap: 15px;
    align-items: center;
`;

const SearchInput = styled.input`
    padding: 8px 12px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    width: 250px;
    
    &:focus {
        outline: none;
        border-color: ${Colors.primaryAccent};
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
`;

const LocationsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
`;

const LocationCard = styled.div`
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: box-shadow 0.2s ease;
    
    &:hover {
        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    }
`;

const LocationCardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
`;

const LocationName = styled.h6`
    color: ${Colors.primaryDark};
    font-weight: 600;
    margin: 0;
    font-size: 16px;
`;

const LocationActions = styled.div`
    display: flex;
    gap: 8px;
`;

const LocationDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const LocationDetail = styled.div`
    color: ${Colors.primaryDark};
    font-size: 14px;
`;

const EmptyState = styled.div`
    grid-column: 1 / -1;
    text-align: center;
    padding: 60px 20px;
    color: #6c757d;
    font-style: italic;
    background: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #dee2e6;
`;

const LocationImage = styled.img`
    width: 100%;
    height: 160px;
    object-fit: cover;
    border-radius: 8px 8px 0 0;
    margin-bottom: 15px;
`;

const ModalTabs = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 10px;
`;

const ModalTabButton = styled.button`
    padding: 8px 16px;
    border: none;
    background: ${props => props.active ? Colors.primaryAccent : 'transparent'};
    color: ${props => props.active ? 'white' : Colors.primaryDark};
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;
    
    &:hover {
        background: ${props => props.active ? Colors.primaryAccent : '#f8f9fa'};
    }
`;

const ImageUploadContainer = styled.div`
    margin-bottom: 20px;
`;

const ImageUploadButton = styled.div`
    padding: 20px;
    border: 2px dashed #ced4da;
    border-radius: 8px;
    text-align: center;
    background: #f8f9fa;
    
    &:hover {
        border-color: ${Colors.primaryAccent};
        background: #e8f4fd;
    }
`;

const ImageGallery = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 15px;
    margin-top: 15px;
`;

const ImagePreviewContainer = styled.div`
    position: relative;
`;

const ImagePreview = styled.img`
    width: 100%;
    height: 120px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e9ecef;
`;

const ImageRemoveButton = styled.button`
    position: absolute;
    top: -5px;
    right: -5px;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    border: none;
    background: #dc3545;
    color: white;
    cursor: pointer;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        background: #c82333;
    }
`;

const EmptyImageState = styled.div`
    grid-column: 1 / -1;
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
    font-style: italic;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px dashed #dee2e6;
`;

const ActivityList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const ActivityItem = styled.div`
    padding: 15px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    background: white;
`;


const ActivityDetails = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
`;

const ActivityDetail = styled.div`
    color: #6c757d;
    font-size: 14px;
`;

const EmptyActivityState = styled.div`
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
    font-style: italic;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px dashed #dee2e6;
`;

const ConfigWarning = styled.div`
    padding: 15px;
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 8px;
    color: #856404;
    margin-bottom: 20px;
    font-size: 14px;
    line-height: 1.4;
`;

const ActivitiesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
`;

const ActivityCard = styled.div`
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: box-shadow 0.2s ease;
    
    &:hover {
        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    }
`;

const ActivityImage = styled.img`
    width: 100%;
    height: 160px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 15px;
`;

const ActivityCardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
`;

const ActivityName = styled.h6`
    color: ${Colors.primaryDark};
    font-weight: 600;
    margin: 0;
    font-size: 16px;
`;

const ActivityActions = styled.div`
    display: flex;
    gap: 8px;
`;

const OptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const OptionCard = styled.div`
    padding: 20px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    background: #f8f9fa;
`;

const OptionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #dee2e6;
`;

const EmptyOptionsState = styled.div`
    text-align: center;
    padding: 30px;
    color: #6c757d;
    font-style: italic;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px dashed #dee2e6;
`; 